import React, { useState, useMemo } from 'react';
import { Mail, Printer, Copy, Check } from 'lucide-react';
import { formatSalonDate } from '../utils/salonDate';

const SchedulingClientCard = ({
    client,
    schedulingDate,
    consolidatedAppointments,
    consolidatePreviousAppointments,
    daysmartIdToName,
    expandedSuggestions,
    clientSuggestions,
    onPrint,
    onEmail,
    onAppointmentClick,
    onLoadSuggestions,
}) => {
    const consolidated = consolidatedAppointments;
    
    const uniquePets = useMemo(() => {
        const pets = new Set();
        consolidated.forEach(a => (a.pet_names || []).forEach(p => pets.add(p)));
        return [...pets];
    }, [consolidated]);
    
    const [selectedPets, setSelectedPets] = useState(null);
    const [copied, setCopied] = useState(false);
    
    const activePets = useMemo(() => selectedPets ?? new Set(uniquePets), [selectedPets, uniquePets]);
    
    const filteredConsolidated = useMemo(() => {
        return consolidated.filter(a => (a.pet_names || []).some(p => activePets.has(p)));
    }, [consolidated, activePets]);
    
    const togglePet = (petName) => {
        setSelectedPets(prev => {
            const current = prev ?? new Set(uniquePets);
            const next = new Set(current);
            if (next.has(petName)) {
                next.delete(petName);
            } else {
                next.add(petName);
            }
            return next;
        });
    };
    
    const formatTime = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    };

    const handleCopy = () => {
        const locationName = client.location_id === 1 ? 'Hopkins' : 'Inver Grove Heights';
        const lines = [`Zoom Groom - ${locationName}`, `Upcoming Appointments for ${client.client_name}`, ''];
        if (filteredConsolidated.length > 0) {
            filteredConsolidated.forEach(appt => {
                const date = new Date(appt.start_time);
                const dateStr = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                lines.push(`${dateStr} at ${timeStr} — ${appt.pet_names.join(' & ')}`);
            });
        } else {
            lines.push('No upcoming appointments scheduled.');
        }
        navigator.clipboard.writeText(lines.join('\n')).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="bg-white rounded-lg shadow overflow-hidden">
            {/* Client Header */}
            <div className={`px-4 py-3 flex items-center justify-between ${
                consolidated.length === 0 
                    ? 'bg-red-50 border-l-4 border-red-500' 
                    : consolidated.length <= 2 
                        ? 'bg-yellow-50 border-l-4 border-yellow-500'
                        : 'bg-green-50 border-l-4 border-green-500'
            }`}>
                <div>
                    <span className="font-semibold text-gray-800">{client.client_name}</span>
                    <span className="ml-2 text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-600">
                        {client.location_id === 1 ? 'Hopkins' : 'Inver Grove'}
                    </span>
                    <span className="ml-2 text-xs text-gray-500">ID: {client.client_id}</span>
                    {client.client_email && (
                        <span className="ml-2 text-xs text-gray-500">{client.client_email}</span>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <div className={`text-sm font-medium ${
                        consolidated.length === 0 
                            ? 'text-red-700' 
                            : consolidated.length <= 2 
                                ? 'text-yellow-700'
                                : 'text-green-700'
                    }`}>
                        {consolidated.length} upcoming appointment{consolidated.length !== 1 ? 's' : ''}
                    </div>
                    <button
                        onClick={() => onPrint(filteredConsolidated)}
                        className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200"
                        title="Print upcoming appointments"
                    >
                        <Printer size={14} />
                        Print
                    </button>
                    <button
                        onClick={handleCopy}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${copied ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        title="Copy appointments to clipboard"
                    >
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                        {copied ? 'Copied!' : 'Copy'}
                    </button>
                    <button
                        onClick={() => onEmail(filteredConsolidated)}
                        className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors bg-blue-100 text-blue-700 hover:bg-blue-200"
                        title="Send appointment email"
                    >
                        <Mail size={14} />
                        Email
                    </button>
                </div>
            </div>
            
            {/* Selected Date's Appointments */}
            <div className="px-4 py-2 bg-purple-50 border-b">
                <div className="text-xs font-medium text-purple-700 mb-1">
                    Appointment(s) on {formatSalonDate(schedulingDate, { month: 'short', day: 'numeric' })}:
                </div>
                <div className="flex flex-wrap gap-2">
                    {client.todayAppointments.map((appt, idx) => (
                        <span key={idx} className="text-sm text-purple-800">
                            {appt.pet_name} - {appt.service_name} @ {formatTime(appt.start_time)}
                            {appt.employee_name && ` (${daysmartIdToName[appt.employee_id] || appt.employee_name})`}
                        </span>
                    ))}
                </div>
            </div>

            {/* Two-column layout for Previous and Upcoming */}
            <div className="grid grid-cols-2 divide-x">
                {/* Previous Appointments (last 5) */}
                <div>
                    <div className="px-4 py-2 bg-gray-100 border-b">
                        <span className="text-xs font-medium text-gray-600">Last 5 Appointments</span>
                    </div>
                    {(() => {
                        const previous = consolidatePreviousAppointments(client.previousAppointments, client.location_id, 5);
                        const formatPreviousDate = (startTime) => (
                            formatSalonDate(startTime, { weekday: 'short', month: 'short', day: 'numeric', year: '2-digit' })
                            || 'Unknown date'
                        );
                        const previousLabel = (appt) => {
                            const petLabel = (Array.isArray(appt.pet_names) && appt.pet_names.length > 0)
                                ? appt.pet_names.filter(Boolean).join(' & ')
                                : (appt.pet_name ? String(appt.pet_name) : '');
                            const serviceLabel = (appt.services || []).filter(Boolean).join(', ');
                            return [petLabel, serviceLabel].filter(Boolean).join(' - ');
                        };
                        return previous.length > 0 ? (
                            <div className="divide-y">
                                {previous.map((appt, idx) => {
                                    const servicesList = previousLabel(appt);
                                    const duration = Number.isFinite(Number(appt.duration)) ? Number(appt.duration) : 0;
                                    return (
                                    <div 
                                        key={idx} 
                                        className="px-4 py-1.5 text-sm hover:bg-gray-100 cursor-pointer"
                                        onClick={() => onAppointmentClick({ appointment_id: appt.ticket_id, location_id: appt.location_id }, true)}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="text-gray-500 w-28 flex-shrink-0">
                                                {formatPreviousDate(appt.start_time)}
                                            </span>
                                            <span className="text-gray-400 w-14 flex-shrink-0 text-xs whitespace-nowrap">
                                                {duration}min
                                            </span>
                                            <span 
                                                className="text-gray-700 truncate cursor-pointer"
                                                title={servicesList}
                                            >
                                                {servicesList}
                                            </span>
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="px-4 py-2 text-sm text-gray-400 italic">
                                No previous appointments
                            </div>
                        );
                    })()}
                </div>

                {/* Upcoming Appointments (consolidated by date) */}
                <div>
                    <div className="px-4 py-2 bg-gray-100 border-b flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-600">Upcoming Appointments</span>
                        {uniquePets.length > 1 && (
                            <div className="flex items-center gap-3">
                                {uniquePets.map(pet => (
                                    <label key={pet} className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={activePets.has(pet)}
                                            onChange={() => togglePet(pet)}
                                            className="rounded text-purple-600 focus:ring-purple-500 h-3 w-3"
                                        />
                                        {pet}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                    {filteredConsolidated.length > 0 ? (
                        <div className="divide-y">
                            {filteredConsolidated.map((appt, idx) => {
                                const servicesList = `${appt.pet_names.join(' & ')} - ${appt.services.join(', ')}`;
                                return (
                                <div 
                                    key={idx} 
                                    className="px-4 py-1.5 text-sm hover:bg-gray-100 cursor-pointer"
                                    onClick={() => onAppointmentClick({ appointment_id: appt.ticket_id, location_id: appt.location_id }, true)}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-gray-700 w-28 flex-shrink-0">
                                            {formatSalonDate(appt.start_time, { weekday: 'short', month: 'short', day: 'numeric' })}
                                        </span>
                                        <span 
                                            className="text-gray-700 truncate cursor-pointer"
                                            title={servicesList}
                                        >
                                            {servicesList}
                                        </span>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="px-4 py-2 text-sm text-red-600 bg-red-50">
                            ⚠️ Needs rebooking!
                        </div>
                    )}
                </div>

                {/* Suggested Appointments */}
                <div>
                    <div 
                        className="px-4 py-2 bg-purple-50 border-b cursor-pointer hover:bg-purple-100 flex items-center justify-between"
                        onClick={onLoadSuggestions}
                    >
                        <span className="text-xs font-medium text-purple-700">✨ Suggested Appointments</span>
                        <span className="text-xs text-purple-500">
                            {expandedSuggestions ? '▼' : '▶'}
                        </span>
                    </div>
                    {expandedSuggestions && (
                        <div className="bg-purple-50/30">
                            {clientSuggestions?.loading && (
                                <div className="px-4 py-2 text-sm text-purple-600">
                                    Analyzing patterns...
                                </div>
                            )}
                            {clientSuggestions?.error && (
                                <div className="px-4 py-2 text-sm text-red-600">
                                    {clientSuggestions.error}
                                </div>
                            )}
                            {clientSuggestions?.data?.suggested_appointments?.length > 0 ? (
                                <div className="divide-y divide-purple-100">
                                    {clientSuggestions.data.suggested_appointments.map((suggestion, idx) => (
                                        <div key={idx} className="px-4 py-2 text-sm">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-purple-700 w-28 flex-shrink-0">
                                                    {formatSalonDate(suggestion.suggested_date, { weekday: 'short', month: 'short', day: 'numeric' })}
                                                </span>
                                                <span className="text-purple-600">
                                                    {suggestion.pet_name} - {suggestion.suggested_services.join(', ')}
                                                </span>
                                            </div>
                                            <div className="text-xs text-purple-500 mt-0.5 ml-[88px]">
                                                {suggestion.reason}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : clientSuggestions?.data && (
                                <div className="px-4 py-2 text-sm text-purple-500 italic">
                                    Not enough history for suggestions
                                </div>
                            )}
                            {clientSuggestions?.data?.pet_patterns?.length > 0 && (
                                <div className="px-4 py-2 border-t border-purple-100">
                                    <div className="text-xs text-purple-600 font-medium mb-1">Visit Patterns:</div>
                                    {clientSuggestions.data.pet_patterns.map((pattern, idx) => (
                                        <div key={idx} className="text-xs text-purple-500">
                                            <span className="font-medium">{pattern.pet_name}:</span>{' '}
                                            {pattern.avg_weeks_between_visits > 0 
                                                ? `~${pattern.avg_weeks_between_visits} weeks between visits`
                                                : 'No pattern detected'}
                                            {pattern.preferred_day_name && ` (prefers ${pattern.preferred_day_name}s)`}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SchedulingClientCard;
